/**
 * A backend route, declared once in v2 (API_ACCESS.md §3.1): the backend's own method and path,
 * its access (`capabilities/access.ts`), and the types of its body, answer and path parameters.
 * Pure. Each file in `routes/` declares the routes of one first path segment.
 */
import type { RouteAccess, RouteRef } from "../capabilities/access";

export type Method = "GET" | "POST" | "PUT" | "DELETE";

/** which of Billing's two bases the route sits under: `bn/` (the default) or the custom `br/` */
export type Base = "bn" | "br";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type -- "no path parameters"
export type NoParams = {};

export interface Route<Body = unknown, Answer = unknown, Params extends object = NoParams> extends RouteRef {
  /** the backend's key, `"POST /sale/save"`: the same in v2, the snapshot, API_ROUTE_MAP.md and a log */
  readonly key: string;
  readonly method: Method;
  /** the backend's path, `:param` segments and all */
  readonly path: string;
  readonly access: RouteAccess;
  readonly base: Base;
  /** the handler's own feature checks worth knowing; the policies enforce them (§3.8) */
  readonly notes?: string;
  /** codes besides `REST_FEATURE_RESTRICTED` that mean a feature check refused (§3.4) */
  readonly restrictedCodes?: readonly string[];
  /** types only: never set */
  readonly __types?: { body: Body; answer: Answer; params: Params };
}

export interface RouteOptions {
  base?: Base;
  notes?: string;
  restrictedCodes?: readonly string[];
}

export function route<Body = unknown, Answer = unknown, Params extends object = NoParams>(
  method: Method,
  path: string,
  access: RouteAccess,
  options: RouteOptions = {},
): Route<Body, Answer, Params> {
  if (!path.startsWith("/")) throw new Error(`api: "${path}" must be the backend's path, starting with "/"`);
  const declared: Route<Body, Answer, Params> = { key: `${method} ${path}`, method, path, access, base: options.base ?? "bn" };
  return Object.assign(declared, options.notes ? { notes: options.notes } : {}, options.restrictedCodes ? { restrictedCodes: options.restrictedCodes } : {});
}

/** the first segment of a path: `/sale/:saleId/print` → `sale` */
export const firstSegment = (path: string): string => path.split("/")[1] ?? "";

/** one file's routes: every path must start with the file's segment (§3.1) */
export function defineRoutes<T extends Record<string, Route<any, any, any>>>(segment: string, routes: T): T {
  for (const [name, declared] of Object.entries(routes)) {
    if (firstSegment(declared.path) !== segment) {
      throw new Error(`api: ${name} (${declared.key}) does not belong in routes/${segment}.ts`);
    }
  }
  return routes;
}

/** the path's `:param` names, in order */
export const paramNames = (path: string): string[] => path.split("/").filter((s) => s.startsWith(":")).map((s) => s.slice(1));

/** Billing's URL for a route: the path without its leading slash, each `:param` filled and encoded */
export function urlFor(declared: Route<any, any, any>, params: Record<string, unknown> = {}): string {
  return declared.path
    .slice(1)
    .split("/")
    .map((segment) => {
      if (!segment.startsWith(":")) return segment;
      const value = params[segment.slice(1)];
      if (value === undefined || value === null || value === "") throw new Error(`api: ${declared.key} needs ${segment}`);
      return encodeURIComponent(String(value));
    })
    .join("/");
}
